import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, MessageCircle, Send, Clock } from "lucide-react";
import { SITE } from "@/lib/site";
import { useSEO } from "@/lib/seo";
import SectionHeading from "@/components/SectionHeading";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  useSEO({
    title: "Contact Us",
    description: "Reach Nari Arogya at Kothrud, Pune. Call, WhatsApp, or visit for Ayurvedic women's healthcare with Dr. Anita Chaudhari.",
    path: "/contact",
  });

  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent! We'll get back within 24 hours.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try WhatsApp or call.");
    } finally {
      setSubmitting(false);
    }
  };

  const waLink = `https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent("Hello Dr. Anita,")}`;

  return (
    <>
      <section className="pt-32 pb-14 lotus-bg" data-testid="contact-hero">
        <div className="na-container text-center">
          <SectionHeading
            eyebrow="Get in Touch"
            title="We'd love to hear from you"
            subtitle="Visit us at our Kothrud clinic, or reach us via phone, WhatsApp or email."
            align="center"
          />
        </div>
      </section>

      <section className="section">
        <div className="na-container grid gap-10 lg:grid-cols-2">
          <div className="space-y-5">
            <div className="na-card p-6 flex gap-4">
              <div className="w-12 h-12 rounded-2xl bg-[color:var(--na-teal)]/10 text-[color:var(--na-teal)] flex items-center justify-center shrink-0">
                <MapPin size={22} />
              </div>
              <div>
                <h3 className="text-xl">Visit the Clinic</h3>
                <p className="text-sm text-[color:var(--na-muted)] mt-1 leading-relaxed">
                  {SITE.address.line1}<br />
                  {SITE.address.line2}<br />
                  {SITE.address.line3}<br />
                  {SITE.address.city} - {SITE.address.pincode}
                </p>
              </div>
            </div>

            <div className="na-card p-6 flex gap-4">
              <div className="w-12 h-12 rounded-2xl bg-[color:var(--na-teal)]/10 text-[color:var(--na-teal)] flex items-center justify-center shrink-0">
                <Phone size={22} />
              </div>
              <div>
                <h3 className="text-xl">Call</h3>
                <a href={`tel:${SITE.phone}`} className="text-sm text-[color:var(--na-muted)] mt-1 hover:text-[color:var(--na-teal)]">{SITE.phone}</a>
              </div>
            </div>

            <div className="na-card p-6 flex gap-4">
              <div className="w-12 h-12 rounded-2xl bg-[#25D366]/10 text-[#25D366] flex items-center justify-center shrink-0">
                <MessageCircle size={22} />
              </div>
              <div>
                <h3 className="text-xl">WhatsApp</h3>
                <a href={waLink} target="_blank" rel="noreferrer" className="text-sm text-[color:var(--na-muted)] mt-1 hover:text-[color:var(--na-teal)]">Chat with us instantly</a>
              </div>
            </div>

            <div className="na-card p-6 flex gap-4">
              <div className="w-12 h-12 rounded-2xl bg-[color:var(--na-teal)]/10 text-[color:var(--na-teal)] flex items-center justify-center shrink-0">
                <Mail size={22} />
              </div>
              <div>
                <h3 className="text-xl">Email</h3>
                <a href={`mailto:${SITE.email}`} className="text-sm text-[color:var(--na-muted)] mt-1 hover:text-[color:var(--na-teal)]">{SITE.email}</a>
              </div>
            </div>

            <div className="na-card p-6 flex gap-4">
              <div className="w-12 h-12 rounded-2xl bg-[color:var(--na-gold)]/10 text-[color:var(--na-gold)] flex items-center justify-center shrink-0">
                <Clock size={22} />
              </div>
              <div>
                <h3 className="text-xl">Consultation Hours</h3>
                <p className="text-sm text-[color:var(--na-muted)] mt-1 leading-relaxed">
                  Mon – Sat: 10:00 AM – 1:30 PM, 5:00 PM – 8:30 PM<br />
                  Sunday: By Appointment
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="na-card p-8" data-testid="contact-form">
            <h3 className="text-3xl">Send us a message</h3>
            <p className="text-sm text-[color:var(--na-muted)] mt-1">We reply within 24 hours.</p>
            <div className="mt-6 space-y-4">
              <Input name="name" placeholder="Your name *" value={form.name} onChange={handleChange} testId="contact-name" />
              <div className="grid gap-4 sm:grid-cols-2">
                <Input name="email" type="email" placeholder="Email *" value={form.email} onChange={handleChange} testId="contact-email" />
                <Input name="phone" placeholder="Phone" value={form.phone} onChange={handleChange} testId="contact-phone" />
              </div>
              <Input name="subject" placeholder="Subject" value={form.subject} onChange={handleChange} testId="contact-subject" />
              <textarea
                name="message"
                placeholder="How can we help you? *"
                value={form.message}
                onChange={handleChange}
                rows={5}
                className="w-full px-4 py-3 rounded-2xl bg-[color:var(--na-section)] border border-[color:var(--na-border)] focus:border-[color:var(--na-teal)] focus:outline-none focus:ring-2 focus:ring-[color:var(--na-teal)]/20 text-sm resize-none"
                data-testid="contact-message"
              />
              <button type="submit" disabled={submitting} className="btn-primary w-full justify-center disabled:opacity-60" data-testid="contact-submit">
                {submitting ? "Sending..." : (<><Send size={16} /> Send Message</>)}
              </button>
            </div>
          </form>
        </div>
      </section>

      <section className="pb-20">
        <div className="na-container">
          <div className="rounded-[2rem] overflow-hidden border border-[color:var(--na-border)]" data-testid="map-embed">
            <iframe
              src={SITE.mapEmbed}
              width="100%"
              height="420"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Nari Arogya Location"
            />
          </div>
        </div>
      </section>
    </>
  );
}

function Input({ name, type = "text", placeholder, value, onChange, testId }) {
  return (
    <input
      name={name}
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      className="w-full px-4 py-3 rounded-2xl bg-[color:var(--na-section)] border border-[color:var(--na-border)] focus:border-[color:var(--na-teal)] focus:outline-none focus:ring-2 focus:ring-[color:var(--na-teal)]/20 text-sm"
      data-testid={testId}
    />
  );
}
